import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Shield, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../lib/api';

interface AuditLog {
  _id: string;
  createdAt: string;
  adminId?: { profile?: { name?: string }; email?: string };
  action: string;
  targetModel?: string;
  targetId?: string;
  changes?: unknown;
}

export default function AuditLogsPage() {
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['audit-logs', page],
    queryFn: async () => {
      const res = await api.get('/admin/audit-logs', { params: { page, limit: 30 } });
      return res.data;
    },
  });

  const logs = data?.data ?? [];
  const pagination = data?.pagination ?? { total: 0, page: 1, totalPages: 1 };

  const actionColor = (action: string) => {
    if (action.includes('delete') || action.includes('ban')) return 'bg-danger-50 text-danger-700';
    if (action.includes('create') || action.includes('approve'))
      return 'bg-success-50 text-success-700';
    if (action.includes('update') || action.includes('edit'))
      return 'bg-warning-50 text-warning-700';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Shield size={24} className="text-primary-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Logs</h1>
          <p className="text-gray-500 mt-1">{pagination.total} total entries</p>
        </div>
      </div>

      <div className="card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-500">Time</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Admin</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Action</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Resource</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Details</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-gray-400">
                    Loading...
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-gray-400">
                    No audit logs
                  </td>
                </tr>
              ) : (
                logs.map((log: AuditLog) => (
                  <tr key={log._id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-gray-500 whitespace-nowrap">
                      {new Date(log.createdAt).toLocaleString()}
                    </td>
                    <td className="py-3 px-4">
                      <span className="font-medium text-gray-900">
                        {log.adminId?.profile?.name || log.adminId?.email || 'System'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`badge ${actionColor(log.action)}`}>{log.action}</span>
                    </td>
                    <td className="py-3 px-4 text-gray-600">
                      {log.targetModel}
                      {log.targetId ? `: ${log.targetId}` : ''}
                    </td>
                    <td className="py-3 px-4 text-gray-500 max-w-xs truncate">
                      {log.changes ? JSON.stringify(log.changes) : '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-500">
            Page {pagination.page} of {pagination.totalPages}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="btn-secondary text-xs flex items-center gap-1"
            >
              <ChevronLeft size={14} /> Prev
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= pagination.totalPages}
              className="btn-secondary text-xs flex items-center gap-1"
            >
              Next <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
